import { http } from '@/core/api'
import { triggerBlobDownload } from '@/shared/composables/use-kpi-query'
import { resolveExcelHeavyResponse } from '@/shared/utils/excel-heavy-operation'

const BASE = '/applications/interview-results/import'

export const INTERVIEW_RESULT_IMPORT_FIELDS = [
  'client_name',
  'factory_name',
  'interview_date',
  'identity_number',
  'result',
  'full_name',
  'phone',
  'fail_reason',
  'start_date',
] as const

/** Matches backend InterviewResultImportColumns::DEFAULT_FAIL_REASON */
export const INTERVIEW_RESULT_IMPORT_DEFAULT_FAIL_REASON = 'Không đủ điều kiện'

export type InterviewResultImportField = (typeof INTERVIEW_RESULT_IMPORT_FIELDS)[number]

export const INTERVIEW_RESULT_IMPORT_BASE_REQUIRED_FIELDS: InterviewResultImportField[] = [
  'identity_number',
  'interview_date',
  'result',
]

/** Required when reconciliation scope has no factory selected. */
export const INTERVIEW_RESULT_IMPORT_FACTORY_REQUIRED_FIELDS: InterviewResultImportField[] = [
  'factory_name',
]

/** @deprecated Prefer requiredFieldsForScope() — kept for callers that need the full default set. */
export const INTERVIEW_RESULT_IMPORT_REQUIRED_FIELDS: InterviewResultImportField[] = [
  ...INTERVIEW_RESULT_IMPORT_FACTORY_REQUIRED_FIELDS,
  ...INTERVIEW_RESULT_IMPORT_BASE_REQUIRED_FIELDS,
]

export function interviewResultImportRequiredFields(options?: {
  clientSiteIds?: number[]
  interviewDate?: string
}): InterviewResultImportField[] {
  const siteIds = (options?.clientSiteIds ?? []).filter((id) => id > 0)
  const hasScopeInterviewDate = Boolean((options?.interviewDate ?? '').trim())
  let fields: InterviewResultImportField[] = [...INTERVIEW_RESULT_IMPORT_BASE_REQUIRED_FIELDS]
  if (hasScopeInterviewDate) {
    fields = fields.filter((field) => field !== 'interview_date')
  }
  if (siteIds.length === 0) {
    fields = [...INTERVIEW_RESULT_IMPORT_FACTORY_REQUIRED_FIELDS, ...fields]
  }
  return fields
}

export type InterviewResultImportOutcome = 'pass' | 'fail' | 'refused' | 'no_show' | 'skip'

export type InterviewResultImportMatchStatus =
  | 'matched'
  | 'suspected'
  | 'unmatched'
  | 'duplicate'
  | 'invalid'
  | 'already_recorded'
  | 'ineligible'
  | 'skipped'

export type InterviewResultImportPreviewRow = {
  excel_row: number
  full_name: string
  identity_number: string
  phone: string
  interview_date: string | null
  result_raw: string
  result: InterviewResultImportOutcome | null
  fail_reason: string
  start_date: string | null
  client_name?: string
  factory_name?: string
  match_status: InterviewResultImportMatchStatus
  matched_by: string | null
  interview_id: number | null
  application_id: number | null
  crm_full_name: string | null
  crm_client_name: string | null
  crm_factory_name: string | null
  crm_job_order_code: string | null
  crm_interview_date: string | null
  crm_interview_status: string | null
  crm_result: string | null
  note: string | null
  can_apply: boolean
  selected: boolean
}

export type InterviewResultImportPreview = {
  file_headers: string[]
  column_mapping: Record<string, string>
  rows: InterviewResultImportPreviewRow[]
  summary: {
    total: number
    matched: number
    suspected: number
    unmatched: number
    duplicate: number
    invalid: number
    already_recorded: number
    ineligible: number
    applyable: number
  }
  header_row?: number
  sheet_preview?: Array<{ row: number; preview: string; cell_count: number }>
}

export type InterviewResultImportHeaders = {
  file_headers: string[]
  column_mapping: Record<string, string>
  result_values: string[]
  result_value_suggestions: Record<string, InterviewResultImportOutcome>
  client_values: string[]
  factory_values: string[]
  header_row: number
  sheet_preview: Array<{ row: number; preview: string; cell_count: number }>
}

function appendColumnMapping(form: FormData, mapping: Record<string, string>) {
  for (const field of INTERVIEW_RESULT_IMPORT_FIELDS) {
    const value = mapping[field]?.trim() ?? ''
    if (value) form.append(`column_mapping[${field}]`, value)
  }
}

function appendResultValueMap(form: FormData, map: Record<string, InterviewResultImportOutcome | ''>) {
  for (const [from, to] of Object.entries(map)) {
    const key = from.trim()
    const target = String(to ?? '').trim()
    if (!key || !['pass', 'fail', 'refused', 'no_show', 'skip'].includes(target)) continue
    form.append(`result_value_map[${key}]`, target)
  }
}

function appendIdValueMap(form: FormData, field: 'client_value_map' | 'factory_value_map', map: Record<string, number | string | ''>) {
  for (const [from, to] of Object.entries(map)) {
    const key = from.trim()
    const id = typeof to === 'number' ? to : Number(to)
    if (!key || !Number.isFinite(id) || id <= 0) continue
    form.append(`${field}[${key}]`, String(id))
  }
}

export const interviewResultImportApi = {
  async downloadTemplate() {
    const { data } = await http.get<Blob>(`${BASE}/template`, { responseType: 'blob' })
    triggerBlobDownload(data, 'mau-import-ket-qua-phong-van.xlsx')
  },

  async detectHeaders(
    file: File,
    options?: {
      columnMapping?: Record<string, string>
      headerRow?: number | null
    },
  ) {
    const form = new FormData()
    form.append('file', file)
    if (options?.columnMapping) appendColumnMapping(form, options.columnMapping)
    if (typeof options?.headerRow === 'number' && options.headerRow > 0) {
      form.append('header_row', String(options.headerRow))
    }
    const { data } = await http.post<{ data: InterviewResultImportHeaders | { operation_id: number; status: string; async?: boolean } }>(`${BASE}/headers`, form, {
      timeout: 180_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    })
    return resolveExcelHeavyResponse<InterviewResultImportHeaders>(data, {
      timeoutMs: 300_000,
    })
  },

  async preview(
    file: File,
    columnMapping: Record<string, string>,
    scope?: {
      clientId?: number | ''
      clientIds?: number[]
      clientSiteId?: number | ''
      clientSiteIds?: number[]
      jobOrderId?: number | ''
      interviewDate?: string
      resultValueMap?: Record<string, InterviewResultImportOutcome | ''>
      clientValueMap?: Record<string, number | string | ''>
      factoryValueMap?: Record<string, number | string | ''>
      headerRow?: number | null
    },
  ) {
    const form = new FormData()
    form.append('file', file)
    appendColumnMapping(form, columnMapping)
    if (scope?.resultValueMap) {
      appendResultValueMap(form, scope.resultValueMap)
    }
    if (scope?.clientValueMap) {
      appendIdValueMap(form, 'client_value_map', scope.clientValueMap)
    }
    if (scope?.factoryValueMap) {
      appendIdValueMap(form, 'factory_value_map', scope.factoryValueMap)
    }
    if (typeof scope?.headerRow === 'number' && scope.headerRow > 0) {
      form.append('header_row', String(scope.headerRow))
    }
    const interviewDate = scope?.interviewDate?.trim() ?? ''
    if (interviewDate) form.append('interview_date', interviewDate)

    const clientIds = [
      ...(scope?.clientIds ?? []),
      ...(typeof scope?.clientId === 'number' && scope.clientId > 0 ? [scope.clientId] : []),
    ].filter((id, index, all) => id > 0 && all.indexOf(id) === index)
    const clientSiteIds = [
      ...(scope?.clientSiteIds ?? []),
      ...(typeof scope?.clientSiteId === 'number' && scope.clientSiteId > 0 ? [scope.clientSiteId] : []),
    ].filter((id, index, all) => id > 0 && all.indexOf(id) === index)

    for (const id of clientIds) {
      form.append('client_ids[]', String(id))
    }
    for (const id of clientSiteIds) {
      form.append('client_site_ids[]', String(id))
    }
    if (clientIds.length === 1) {
      form.append('client_id', String(clientIds[0]))
    }
    if (clientSiteIds.length === 1) {
      form.append('client_site_id', String(clientSiteIds[0]))
    }
    if (typeof scope?.jobOrderId === 'number' && scope.jobOrderId > 0) {
      form.append('job_order_id', String(scope.jobOrderId))
    }
    const { data } = await http.post<{ data: InterviewResultImportPreview | { operation_id: number; status: string; async?: boolean } }>(`${BASE}/preview`, form, {
      timeout: 180_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    })
    return resolveExcelHeavyResponse<InterviewResultImportPreview>(data, {
      timeoutMs: 300_000,
    })
  },
  async apply(rows: Array<{
    interview_id: number
    result: Exclude<InterviewResultImportOutcome, 'skip'>
    fail_reason?: string
    start_date?: string | null
  }>) {
    const { data } = await http.post<{
      data: {
        applied: number
        failed: number
        errors: Array<{ interview_id: number; message: string }>
      } | { operation_id: number; status: string; async?: boolean }
      message?: string
    }>(`${BASE}/apply`, { rows }, {
      timeout: 180_000,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
    })
    const result = await resolveExcelHeavyResponse<{
      applied: number
      failed: number
      errors: Array<{ interview_id: number; message: string }>
    }>(data, { timeoutMs: 300_000 })
    return {
      data: result,
      message: undefined as string | undefined,
    }
  },
}
